import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, FileText, Folder, Clock, Import as Export } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function PostsPage() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-4">Blog Posts</h1>
        <p className="text-lg text-gray-700 mb-6">
          After generating content with the AI Writer, you can manage, organize, and export your blog posts in various ways. This section explains how to work with your saved posts.
        </p>
      </div>

      <div className="prose max-w-none">
        <h2>Posts Overview</h2>
        <p>
          All posts you generate are automatically saved to your account. You can access them at any time from the "My Posts" section, accessible from the top navigation menu.
        </p>
        
        <div className="not-prose">
          <img 
            src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?q=80&w=1200&auto=format&fit=crop" 
            alt="Posts interface" 
            className="w-full h-64 object-cover rounded-lg my-6"
          />
        </div>
        
        <h3>Post List View</h3>
        <p>
          The post list view shows all your generated posts in reverse chronological order (newest first). Each post entry displays:
        </p>
        
        <ul>
          <li>Post title</li>
          <li>Creation date</li>
          <li>Preview of the first few lines</li>
          <li>Options to view, edit, or delete the post</li>
        </ul>

        <h3>Post Detail View</h3>
        <p>
          Clicking on a post in the list opens the post detail view, where you can:
        </p>
        
        <ul>
          <li>Read the full content</li>
          <li>Copy the entire post to your clipboard</li>
          <li>Export to third-party platforms</li>
          <li>Generate a new post based on similar parameters</li>
        </ul>
      </div>

      <div className="bg-white border rounded-lg p-6 mt-8">
        <h2 className="text-xl font-bold mb-4">Organizing Your Posts</h2>
        
        <div className="bg-gray-50 p-4 rounded-md border mb-6">
          <div className="flex items-center gap-3 mb-3">
            <Folder className="w-5 h-5 text-blue-700" />
            <h3 className="font-semibold">Post Groups</h3>
          </div>
          
          <p className="text-gray-700 mb-4">
            You can organize your posts into groups to make them easier to find and manage. Groups function like folders or categories for your content.
          </p>
          
          <div className="space-y-3 mb-4">
            <h4 className="font-medium text-sm">To create a group:</h4>
            <ol className="list-decimal ml-5 text-sm text-gray-600 space-y-1">
              <li>Click the "Create Group" button in the sidebar</li>
              <li>Enter a name for your group</li>
              <li>Click "Create"</li>
            </ol>
          </div>
          
          <div className="space-y-3">
            <h4 className="font-medium text-sm">To add a post to a group:</h4>
            <ol className="list-decimal ml-5 text-sm text-gray-600 space-y-1">
              <li>Drag and drop a post from the list onto a group in the sidebar</li>
              <li>Or use the "Move to group" option in the post's menu</li>
            </ol>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-md border">
          <div className="flex items-center gap-3 mb-3">
            <Clock className="w-5 h-5 text-blue-700" />
            <h3 className="font-semibold">Post History</h3>
          </div>
          
          <p className="text-gray-700 mb-4">
            All generated posts are automatically saved with a timestamp. This allows you to:
          </p>
          
          <ul className="list-disc ml-5 text-sm text-gray-600 space-y-2">
            <li>Track when content was created</li>
            <li>Sort posts by creation date</li>
            <li>Maintain a history of all your content</li>
          </ul>
        </div>
      </div>
      
      <div className="bg-white border rounded-lg p-6 mt-8">
        <div className="flex items-center gap-3 mb-4">
          <Export className="w-5 h-5 text-blue-700" />
          <h2 className="text-xl font-bold">Exporting Your Content</h2>
        </div>
        
        <p className="text-gray-700 mb-6">
          AISEOBlogGenerator makes it easy to use your content in various platforms and formats.
        </p>
        
        <div className="grid gap-4 md:grid-cols-2">
          <div className="border rounded p-4">
            <h3 className="font-medium mb-2">Copy to Clipboard</h3>
            <p className="text-sm text-gray-600 mb-3">
              The simplest way to export your content is to copy it to clipboard and paste it anywhere.
            </p>
            <div className="bg-gray-50 p-2 rounded text-sm">
              <span className="text-gray-500">Button:</span> Copy Content
            </div>
          </div>
          
          <div className="border rounded p-4">
            <h3 className="font-medium mb-2">Export to Notion</h3>
            <p className="text-sm text-gray-600 mb-3">
              Send your content directly to a new Notion page with a single click.
            </p>
            <div className="bg-gray-50 p-2 rounded text-sm">
              <span className="text-gray-500">Button:</span> Notion
            </div>
          </div>
          
          <div className="border rounded p-4">
            <h3 className="font-medium mb-2">Export to Google Docs</h3>
            <p className="text-sm text-gray-600 mb-3">
              Open a new Google Doc with your content pre-populated.
            </p>
            <div className="bg-gray-50 p-2 rounded text-sm">
              <span className="text-gray-500">Button:</span> Google Docs
            </div>
          </div>
          
          <div className="border rounded p-4">
            <h3 className="font-medium mb-2">Markdown Format</h3>
            <p className="text-sm text-gray-600 mb-3">
              All content is formatted with proper Markdown, making it compatible with most platforms.
            </p>
            <div className="bg-gray-50 p-2 rounded text-sm">
              <span className="text-gray-500">Headings:</span> #, ##, ###<br/>
              <span className="text-gray-500">Bold:</span> **text**<br/>
              <span className="text-gray-500">Lists:</span> *, 1., etc.
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-8 pt-6 border-t">
        <Button variant="outline" asChild>
          <Link to="/docs/writer/ai-models">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back: AI Models
          </Link>
        </Button>
        <Button asChild>
          <Link to="/docs/posts/managing">
            Next: Managing Posts
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
}